import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useMatches } from "react-router-dom";
import { X } from "lucide-react";

import { DemoModeBanner } from "./DemoModeBanner";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { ModuleSidebar } from "./ModuleSidebar";
import { RecordBar } from "./RecordBar";
import { LauncherSkeleton } from "@/pages/launcher/LauncherSkeleton";
import { Topbar } from "./Topbar";
import { MaxwellActionProvider } from "@/components/maxwell/maxwell-actions";
import { MaxwellRail } from "@/components/maxwell/MaxwellRail";
import { MaxwellEntryButton } from "@/components/layout/MaxwellEntryButton";
import { Button } from "@/components/ui/button";
import { ConfirmProvider } from "@/components/ui/confirm";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useSession } from "@/hooks/useSession";
import { moduleOrDefault } from "@/lib/modules";
import { useResolvedModuleKey } from "@/lib/module-context";
import { setActiveUnits, unitPrefsKey } from "@/lib/units";
import { setViewingAsBar } from "@/lib/top-chrome";
import { useAdoptBrowserPreferences, useApplyPreferences } from "@/hooks/useApplyPreferences";
import { readBrowserPreferences, resolvePreferences } from "@/lib/preferences";
import { useResolvedPageMeta } from "@/hooks/useResolvedPageMeta";
import { PageMetaContext, type PageMeta } from "@/lib/page-meta";
import { useMaxwellAccess } from "@/components/maxwell/useMaxwellAccess";
import { SessionProvider } from "@/lib/session-context";
import { PreferencesDialogProvider } from "@/components/layout/preferences/PreferencesDialogProvider";
import { SidebarLayoutProvider } from "@/lib/sidebar-layout";
import { usePresentMode } from "@/pages/energy-advisor/context";

/**
 * Root layout. Desktop (lg+): fixed-width sidebar beside main column.
 * Mobile: sidebar slides in as an off-canvas drawer, toggled from the topbar.
 *
 * Also owns:
 *  · the PageMetaContext provider (so pages can call usePageMeta)
 *  · a single document.title effect (so no page needs a useEffect for this)
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [override, setOverride] = useState<PageMeta | null>(null);
  const location = useLocation();
  const { presentMode, setPresentMode } = usePresentMode();

  // The shell is the *only* call site for useSession(). Every descendant
  // reads the session via SessionContext (useAuthSession), so we don't fan
  // out duplicate React Query subscriptions and the session is guaranteed
  // present below this point.
  const { data: session, isLoading } = useSession();
  const matches = useMatches();
  const bare = matches.some(
    (m) =>
      typeof m.handle === "object" &&
      m.handle != null &&
      (m.handle as { bare?: boolean }).bare === true,
  );
  // The matched route's own app, if it has one. Read from the deepest match
  // that declares it so a tab child inherits its parent's app. Shared record
  // routes declare none — `useResolvedModuleKey` then falls back to the app the
  // user was last in, which is what makes drilling into a client keep you in
  // Energy Advisor (or Market Intelligence) instead of teleporting you elsewhere.
  const routeModuleKey = matches.reduce<string | undefined>((acc, m) => {
    if (typeof m.handle !== "object" || m.handle == null) return acc;
    const key = (m.handle as { moduleKey?: string }).moduleKey;
    return key ?? acc;
  }, undefined);
  const routeDemoSafe = matches.some(
    (m) =>
      typeof m.handle === "object" &&
      m.handle != null &&
      (m.handle as { demoSafe?: boolean }).demoSafe === true,
  );
  const activeModuleKey = useResolvedModuleKey(routeModuleKey);
  const activeModule = moduleOrDefault(activeModuleKey);
  const SidePanel = activeModule.sidePanel;

  // Close the mobile drawer whenever the route changes. Track the previous
  // pathname in state and reset during render — avoids the cascading-render
  // warning that setState-in-effect produces.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setMobileNavOpen(false);
  }

  const setOverrideStable = useCallback((meta: PageMeta | null) => {
    setOverride(meta);
  }, []);

  const pageMetaCtx = useMemo(
    () => ({ override, setOverride: setOverrideStable }),
    [override, setOverrideStable],
  );

  // The user's display preferences live on the server (`session.preferences`).
  // Adopt any this browser still holds from before they moved there — first,
  // so it reads them before anything is re-applied — then put the session's
  // look on `<html>` (the pre-paint script drew the last cached look until
  // the session resolved). Both run before the session gate below so the
  // hook order never changes.
  useAdoptBrowserPreferences(session);
  useApplyPreferences(session);

  // Hold rendering until the session resolves. Without this gate the shell
  // would paint (sidebar, topbar, skeletons) for one frame while
  // `/api/session` is in flight — and on a 401, `lib/api.ts` redirects to
  // `/api/auth/start`, producing a visible flash of authenticated chrome
  // before the redirect lands. Rendering a blank background while loading
  // or unresolved keeps the unauthenticated → login bounce silent.
  if (isLoading || !session) {
    // The launcher gets a full-page skeleton rather than an empty screen;
    // every other route keeps the bare background.
    //
    // Scoped by `bare` (true only for `/dashboard`) because the flag isn't
    // known yet — the session is what carries it — so this can't ask whether
    // the PowerUp launcher or `main`'s dashboard is the destination. `bare`
    // is a static route property, available immediately, and only the
    // launcher sets it. Flag-off `/dashboard` briefly shows this too; the
    // silhouettes (greeting, KPI strip, chart pair) are close enough that
    // it reads as the page loading either way.
    //
    // The skeleton fades itself in after a beat, so a signed-out visitor
    // mid-redirect to `/api/auth/start` never sees it — on a 401 the session
    // promise never resolves and `isLoading` stays true throughout. See
    // `LauncherSkeleton`.
    if (bare) return <LauncherSkeleton />;
    return <div className="min-h-screen bg-background" />;
  }

  // Publish the reader's units (`lib/units.ts`) before any descendant renders,
  // for code with no React to hook into — chart formatters, the pre-`useUnits`
  // helpers in `lib/format.ts`. The session gate above guarantees this runs
  // with a resolved session, so every rate and usage paints in the reader's
  // units and precision on first render.
  const { units } = resolvePreferences(session, readBrowserPreferences(session.user_id));
  setActiveUnits(units);
  // Same reason: everything pinned to the top reads the "Viewing as" bar's
  // room from here, so it must be set before any of it renders.
  setViewingAsBar(session.impersonating);
  // Module state has no subscribers, so key the page on the unit choices: a
  // unit change in Preferences remounts the open page and it repaints in the
  // new units at once. Decimals stay off the key: this component re-renders on
  // any preference change and republishes the units above, so a decimals click
  // repaints in place and the page keeps its unsaved edits. (`useUnits()`
  // re-renders on its own; this covers everything else.)
  const pageKey = unitPrefsKey(units);

  // Demo tenants are confined to Energy Advisor. Keyed on the route's OWN
  // module so a shared record (no `moduleKey`) is never bounced — see below.
  // A page that masks client names in Demo Mode (`demoSafe`) stays open.
  const demoBounce =
    session.demo_mode &&
    routeModuleKey !== undefined &&
    routeModuleKey !== "energy-advisor" &&
    !routeDemoSafe;

  return (
    <SessionProvider session={session}>
      {/* Above the units-keyed page, so Preferences stays open while a unit
          change remounts the page behind it. */}
      <PreferencesDialogProvider>
        <PageMetaContext.Provider value={pageMetaCtx}>
          <TooltipProvider delayDuration={200}>
            <ConfirmProvider>
              <SidebarLayoutProvider>
                {/* Hoisted above every shell branch — not just Energy Advisor's — so
                    `useRegisterMaxwellActions` is safe from any route, per
                    this provider's own contract (see `MaxwellRailSlot`'s doc
                    comment). Maxwell is heading toward spanning every app,
                    not staying advisor-only, so the action bus needs to be
                    live regardless of which shell is currently rendering;
                    only the *rail UI* stays advisor-scoped for now (rendered
                    via `MaxwellRailSlot`, that branch only, below). The
                    floating entry button (`MaxwellFloatingButtonSlot`, right
                    after this provider closes) IS shell-wide — it's a fixed
                    overlay, not part of any shell's layout, so it renders
                    once outside the branch chain and sits on top of whatever
                    shell is active underneath. */}
                <MaxwellActionProvider>
                  {demoBounce ? (
                    // Demo tenants get a sandboxed advisor view, no sidebar
                    // and no way out (see the demo-mode note below), so they
                    // have no business in a second app. Bounce to the
                    // dashboard-first home, same as any other unauthorized hit.
                    //
                    // Keyed on the *route's own* app, not the resolved one: a
                    // shared record (a client) carries no `moduleKey` and must NOT
                    // bounce — part of the advisor surface a demo tenant is
                    // allowed to see. The previous `globalShell || reportsShell`
                    // predicate would have bounced every client page the moment
                    // records stopped being module-owned routes.
                    <Navigate to="/dashboard" replace />
                  ) : bare ? (
                    // No sidebar, no topbar, no chrome at all — the route owns
                    // the entire viewport. Distinct from `chromeless` (which
                    // only skips <main>'s padding but keeps Sidebar/Topbar):
                    // use `bare` for a standalone full-screen surface like the
                    // dashboard-first launcher at `/dashboard`. Demo-mode
                    // tenants reach this branch too — there's nothing to bounce
                    // them away from, since `LauncherPage` renders their own
                    // dashboard same as it does for anyone else.
                    //
                    // The "Viewing as" bar is the one piece of chrome every
                    // branch keeps, pinned across the very top: a super admin
                    // viewing as someone must always see it, and the way back.
                    <div className="min-h-screen bg-background text-foreground">
                      <ImpersonationBanner />
                      <Outlet key={pageKey} />
                    </div>
                  ) : presentMode ? (
                    <div className="flex min-h-screen flex-col bg-background text-foreground">
                      <ImpersonationBanner />
                      <PresentationBrandBand onExit={() => setPresentMode(false)} />
                      <div className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col px-6 py-5">
                        <Outlet key={pageKey} />
                      </div>
                    </div>
                  ) : (
                    // One branch for every app. Which sidebar renders is a
                    // registry lookup on the resolved app key — replacing the
                    // old per-shell ternary cascade (`globalShell ? … :
                    // reportsShell ? … : Sales`), which needed a new branch,
                    // a new boolean, and a new near-identical sidebar per app.
                    //
                    // The "Viewing as" bar spans the full width above the
                    // sidebar and topbar, which pin just below it
                    // (`top-viewing-as`).
                    <div className="flex min-h-screen flex-col bg-background text-foreground">
                      <ImpersonationBanner />
                      <div className="flex flex-1">
                        {/* Demo mode hides the sidebar entirely — no rail, no
                            drawer, no way back to it — mirroring present mode's
                            chromeless surface. Exit via the demo banner. */}
                        {!session.demo_mode && (
                          <ModuleSidebar
                            mod={activeModule}
                            mobileOpen={mobileNavOpen}
                            onClose={() => setMobileNavOpen(false)}
                          />
                        )}
                        <div className="flex min-w-0 flex-1 flex-col">
                          <Topbar
                            onOpenMobileNav={() => setMobileNavOpen(true)}
                            sidebarHidden={session.demo_mode}
                            inAdminApp={activeModule.key === "admin"}
                            moduleAction={activeModule.topbarAction}
                          />
                          {/* Shared records live here, outside the module's
                              sidebar, in the modules that opt in (`recordBar`:
                              Energy Advisor and Operations). Hidden in demo
                              mode along with the rest of the cross-module chrome. */}
                          {!session.demo_mode && activeModule.recordBar && <RecordBar />}
                          <DemoModeBanner />
                          <div className="flex min-h-0 min-w-0 flex-1">
                            <ShellOutlet pageKey={pageKey} />
                            {/* A module's side panel (Market Intelligence's "My
                                views"): under the top bar, beside the page alone,
                                pushing it left. Renders nothing while closed. */}
                            {!session.demo_mode && SidePanel && <SidePanel />}
                            {/* Rail stays Energy-Advisor-scoped; the action bus above and
                                the floating button below are shell-wide. */}
                            {activeModule.key === "energy-advisor" && <MaxwellRailSlot />}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </MaxwellActionProvider>
                <MaxwellFloatingButtonSlot />
                <DocumentTitleSync />
                <Toaster />
              </SidebarLayoutProvider>
            </ConfirmProvider>
          </TooltipProvider>
        </PageMetaContext.Provider>
      </PreferencesDialogProvider>
    </SessionProvider>
  );
}

/**
 * Thin top band rendered above the page content when present mode is
 * on. Mirrors the band the deprecated `/p/energy-advisor/:token` shared-link
 * viewer used to render so the in-app present-mode surface matches the
 * customer-facing view it replaces, with the addition of an obvious
 * "Exit present mode" affordance — the only thing the shared link
 * itself never needed.
 */
function PresentationBrandBand({ onExit }: { onExit: () => void }) {
  return (
    <div className="border-b border-border bg-card px-6 py-2">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
          Pilot Energy · Energy Advisor presentation
        </div>
        <Button variant="outline" size="sm" onClick={onExit}>
          <X size={13} />
          Exit present mode
        </Button>
      </div>
    </div>
  );
}

/**
 * Renders the active route's element with one of two wrappers depending
 * on the route's `chromeless` handle flag:
 *
 *   - **Standard**: `<main>` with the app's default padding + scroll —
 *     every regular page renders inside this.
 *   - **Chromeless**: a flex-column container with no padding, taking
 *     the remaining height of the right column. Page is responsible for
 *     its own scroll container + footer. Same structural pattern as
 *     `<Topbar>` itself — the chromeless page becomes a sibling of the
 *     topbar inside the right column rather than a descendant of main.
 *
 * Chromeless mode is the right answer for pages with a docked footer
 * (RFQ walkthrough StepFooter) — the footer is a regular flex child of
 * the page, sits at the column's bottom edge naturally, no sticky/fixed
 * tricks, no negative-margin escapes from main's padding.
 */
function ShellOutlet({ pageKey }: { pageKey: string }) {
  const matches = useMatches();
  const chromeless = matches.some(
    (m) =>
      typeof m.handle === "object" &&
      m.handle != null &&
      (m.handle as { chromeless?: boolean }).chromeless === true,
  );
  if (chromeless) {
    return (
      <div className="flex min-w-0 flex-1 flex-col bg-background">
        <Outlet key={pageKey} />
      </div>
    );
  }
  return (
    <main className="min-w-0 flex-1 px-5 pb-8 pt-6 md:px-7 md:pt-7 lg:px-8">
      <Outlet key={pageKey} />
    </main>
  );
}

/** Renders the Maxwell rail only when the caller can reach Maxwell, and only
 *  in the Energy Advisor shell branch (the rail UI is still scoped there). Lives under
 *  the session providers so the access check resolves. `MaxwellActionProvider`
 *  itself is hoisted above every shell branch in `AppShell`, so pages'
 *  `useRegisterMaxwellActions` is safe from any route, not just that one. */
function MaxwellRailSlot() {
  return useMaxwellAccess() ? <MaxwellRail /> : null;
}

/** Renders the floating Maxwell entry button globally — every shell branch,
 *  not just Energy Advisor (unlike `MaxwellRailSlot`, which stays scoped).
 *  Gated on the same feature-and-permission pair the `/maxwell` route itself
 *  requires, so the button never appears for a caller who would just bounce
 *  on click. */
function MaxwellFloatingButtonSlot() {
  return useMaxwellAccess() ? <MaxwellEntryButton /> : null;
}

function DocumentTitleSync() {
  const { title, breadcrumbs } = useResolvedPageMeta();

  useEffect(() => {
    const leaf = title ?? breadcrumbs?.[breadcrumbs.length - 1]?.label;
    document.title = leaf ? `PowerUp — ${leaf}` : "PowerUp";
  }, [title, breadcrumbs]);

  return null;
}
